import { useState, useCallback, useMemo } from 'react';
import { useSalesModal as useGlobalSalesModal } from '../../../hooks/useSalesModal';
import { formatCurrency } from '../../../utils/formatters/money';
import { formatDate } from '../../../utils/formatters/date';
import type { SalesOrderCompletedPayload } from '../SalesStep3/SalesStep3.hooks';
import { SALES_CUSTOMER_STEP_ENABLED } from '../../../config/feature-flags';
import {
  buildPrintChromeHtml,
  escapeHtml,
  openPrintWindow,
  PRINT_NO_PRINT_CSS,
  PRINT_POPUP_BLOCKED_MESSAGE,
} from '../../../utils/printWindow';

export const useSalesModal = () => {
  const totalSteps = SALES_CUSTOMER_STEP_ENABLED ? 4 : 3;
  const { state, isOpen, closeModal, calculateOrderSummary, finish } = useGlobalSalesModal();
  const {
    currentStep,
    selectedLocation,
    selectedProducts,
    selectedCustomer,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    discountConfig,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    paymentConfig,
  } = state;

  // Local state for the modal
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isProcessing, setIsProcessing] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const [orderNumber, setOrderNumber] = useState('');
  const [branchReceiptNumber, setBranchReceiptNumber] = useState<string | null>(null);
  const [orderCompletedAt, setOrderCompletedAt] = useState('');
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [showCancelConfirmation, setShowCancelConfirmation] = useState(false);

  // Handle order completion from SalesStep3
  const handleOrderCompleted = useCallback((payload: SalesOrderCompletedPayload) => {
    setOrderNumber(payload.orderCode);
    setBranchReceiptNumber(payload.branchReceiptCode ?? null);
    setOrderCompletedAt(payload.completedAt);
    setIsCompleted(true);
  }, []);

  // Handle modal close (internal - used when confirmed)
  const handleClose = useCallback(() => {
    if (!isProcessing) {
      closeModal();
      setIsCompleted(false);
      setOrderNumber('');
      setBranchReceiptNumber(null);
      setOrderCompletedAt('');
      setShowPrintModal(false);
    }
  }, [isProcessing, closeModal]);

  // Handle close click (show confirmation if there are changes)
  const handleCloseClick = useCallback(() => {
    if (!isProcessing && !isCompleted) {
      // Mostrar confirmación si hay cambios (no está completado ni procesando)
      setShowCancelConfirmation(true);
    } else {
      // Si está completado o procesando, cerrar sin confirmación
      handleClose();
    }
  }, [isProcessing, isCompleted, handleClose]);

  // Cancel operation (close modal after confirmation)
  const cancelOperation = useCallback(() => {
    setShowCancelConfirmation(false);
    closeModal();
    setIsCompleted(false);
    setOrderNumber('');
    setBranchReceiptNumber(null);
    setOrderCompletedAt('');
    setShowPrintModal(false);
  }, [closeModal]);

  // Handle print receipt
  const handlePrintReceipt = useCallback(() => {
    setShowPrintModal(true);
  }, []);

  // Handle print
  const handlePrint = useCallback(() => {
    const printContent = document.getElementById('receipt-content');
    if (!printContent) return;

    const html = `<!DOCTYPE html>
<html>
  <head>
    <meta charset="utf-8"/>
    <title>Comprobante de Venta - ${escapeHtml(orderNumber)}</title>
    <style>
      body { font-family: 'Courier New', monospace; margin: 0; padding: 20px; background: white; }
      .receipt { max-width: 300px; margin: 0 auto; }
      .text-center { text-align: center; }
      .text-right { text-align: right; }
      .border-top { border-top: 1px dashed #000; margin: 10px 0; padding-top: 10px; }
      .font-bold { font-weight: bold; }
      .text-lg { font-size: 18px; }
      .text-sm { font-size: 12px; }
      .mb-2 { margin-bottom: 8px; }
      .mb-4 { margin-bottom: 16px; }
      .flex { display: flex; justify-content: space-between; }
      .w-full { width: 100%; }
      @media print {
        body { margin: 0; padding: 10px; }
      }
      ${PRINT_NO_PRINT_CSS}
    </style>
  </head>
  <body>
    ${printContent.innerHTML}
    ${buildPrintChromeHtml()}
  </body>
</html>`;

    const printWindow = openPrintWindow({
      html,
      size: 'ticket',
      windowName: 'printSalesReceipt',
    });

    if (!printWindow) {
      window.alert(PRINT_POPUP_BLOCKED_MESSAGE);
    }
  }, [orderNumber]);

  // Handle finish
  const handleFinish = useCallback(() => {
    finish();
    setIsCompleted(false);
    setOrderNumber('');
    setBranchReceiptNumber(null);
    setOrderCompletedAt('');
    setShowPrintModal(false);
  }, [finish]);

  // Close print modal
  const handleClosePrintModal = useCallback(() => {
    setShowPrintModal(false);
  }, []);

  // Calculate progress percentage
  const progressPercentage = useMemo(
    () => (currentStep / totalSteps) * 100,
    [currentStep, totalSteps],
  );

  // Get step title
  const stepTitle = useMemo(() => {
    if (SALES_CUSTOMER_STEP_ENABLED) {
      switch (currentStep) {
        case 1:
          return 'Seleccionar piezas';
        case 2:
          return 'Descuentos y Métodos de Pago';
        case 3:
          return 'Cliente';
        case 4:
          return 'Confirmación de Venta';
        default:
          return 'Seleccionar piezas';
      }
    }

    switch (currentStep) {
      case 1:
        return 'Seleccionar piezas';
      case 2:
        return 'Descuentos y Métodos de Pago';
      case 3:
        return 'Confirmación de Venta';
      default:
        return 'Seleccionar piezas';
    }
  }, [currentStep]);

  // Format grand total
  const formattedGrandTotal = useMemo(
    () => formatCurrency(calculateOrderSummary?.grandTotal ?? 0),
    [calculateOrderSummary],
  );

  const formattedOrderCompletedAt = useMemo(
    () => (orderCompletedAt ? formatDate(orderCompletedAt) : ''),
    [orderCompletedAt],
  );

  // Handle close confirmation modal
  const handleCloseConfirmation = useCallback(() => {
    setShowCancelConfirmation(false);
  }, []);

  return {
    // State
    isOpen,
    currentStep,
    isProcessing,
    isCompleted,
    orderNumber,
    branchReceiptNumber,
    orderCompletedAt,
    formattedOrderCompletedAt,
    showPrintModal,
    showCancelConfirmation,
    totalSteps,
    selectedLocation,
    selectedProducts,
    selectedCustomer,
    progressPercentage,
    stepTitle,
    formattedGrandTotal,
    calculateOrderSummary,
    // Handlers
    handleOrderCompleted,
    handleClose,
    handleCloseClick,
    cancelOperation,
    handleCloseConfirmation,
    handlePrintReceipt,
    handlePrint,
    handleFinish,
    handleClosePrintModal,
  };
};
